import { Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { useState, useRef, useEffect } from 'react';

export default function LanguageSelector() {
  const { currentLanguage, changeLanguage, isInitialized, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getLanguageFlag = (langCode: string) => {
    switch (langCode) {
      case 'en':
        return '🇺🇸';
      case 'es':
        return '🇪🇸';
      case 'it':
        return '🇮🇹';
      default:
        return '🌐';
    }
  };

  const handleLanguageChange = (langCode: string) => {
    if (isInitialized) {
      changeLanguage(langCode);
      setIsOpen(false);
    } else {
      console.warn('Language selector not initialized yet');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Default to 'it' if currentLanguage is undefined
  const displayLanguage = currentLanguage || 'it';

  // If not initialized, we can show a loading state or just the current language
  if (!isInitialized) {
    return (
      <div className="flex items-center px-3 py-2">
        <Globe className="w-4 h-4 mr-2" />
        <span className="text-sm">{displayLanguage.toUpperCase()}</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium hover:bg-accent transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Seleziona lingua / Select language"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{getLanguageFlag(displayLanguage)}</span>
        <span className="hidden sm:inline">{displayLanguage.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 opacity-60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50 overflow-hidden">
          <div className="py-1">
            {Object.entries(languages).map(([code, name]) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code)}
                className={`w-full text-left px-4 py-2 text-sm ${
                  displayLanguage === code 
                    ? 'bg-primary text-primary-foreground font-semibold' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="mr-2">{getLanguageFlag(code)}</span>
                {name} ({code.toUpperCase()})
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}